import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status, Refresh2 } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import type { Locale, DateRange } from "@/shared/utils";
import type { ContractorPayrollAggregatedStatus } from "../../../types";
import type { StatusOption } from "../../../hooks";

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  statusFilter: ContractorPayrollAggregatedStatus | null;
  onStatusChange: (v: ContractorPayrollAggregatedStatus | null) => void;
  statusOptions: StatusOption[];
  locale: Locale;
  hasActiveFilters: boolean;
  onReset: () => void;
}

export default function ContractorPayrollFilter({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  locale,
  hasActiveFilters,
  onReset,
}: Props) {
  const { t } = useTranslation("ContractorPayrollPage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center gap-2 mb-7">
      <div className="min-w-48">
        <Input
          isSmall
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("filters.searchPlaceholder")}
          icon={<SearchNormal1 size={14} color="currentColor" />}
        />
      </div>

      <DatePicker
        value={dateRange}
        mode="range"
        onChange={handleDateRangeChange}
        locale={locale}
        placeholder={t("filters.allTime")}
        isSmall
        hasPeriodPicker
        width="min-w-40"
        icon={<Calendar2 size={16} color="currentColor" />}
        align="left"
      />

      <div className="min-w-40">
        <Select<ContractorPayrollAggregatedStatus | null>
          options={statusOptions}
          placeholder={t("filters.allStatuses")}
          value={statusFilter}
          onChange={onStatusChange}
          isSmall
          icon={<Status size={16} color="currentColor" />}
          width="w-56"
        />
      </div>

      {hasActiveFilters && (
        <button
          className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
          onClick={onReset}>
          <Refresh2 size={16} color="currentColor" />
          <span className="text-label-sm">{t("filters.reset")}</span>
        </button>
      )}
    </div>
  );
}
